import { useQueryClient } from "@tanstack/react-query";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import { CommonModels } from "@/data/common/common.models";
import { ProfileAnalyticsQueries } from "@/data/profileAnalytics/profileAnalytics.queries";
import { WorthDataQueries } from "@/data/worthData/worthData.queries";
import { useToast } from "@/hooks/useToast";
import { useOverlayQueueStore } from "@/providers/overlay/overlay-queue.store";

/**
 * Hook to detect if worth-impacting changes were made and provide recalculation mutation
 */
export const useWorthDataChangeDetection = () => {
  const queryClient = useQueryClient();
  const { successToast, errorToast } = useToast();
  const { enqueueOverlay } = useOverlayQueueStore();

  const { mutateAsync: calculateWorthData, isPending: isRecalculating } = WorthDataQueries.useCalculate({
    onSuccess: () => {
      successToast({
        text: "Worth score recalculation started",
      });
    },
    onError: (error) => {
      if (error instanceof Error && error.message.includes("Quota exceeded")) {
        // Enqueue quota exceeded modal with highest priority
        enqueueOverlay({
          id: "quota-exceeded-modal",
          type: "quota-exceeded",
          priority: 0, // Highest priority
        });
      } else {
        errorToast({
          text: "There was an error recalculating your worth score, try again later.",
        });
      }
    },
  });

  /**
   * Check if any worth-impacting fields were changed
   * @param dirtyFields - React Hook Form's dirtyFields object
   * @returns boolean indicating if worth-impacting changes were made
   */
  const hasWorthImpactingChanges = <T extends FieldValues>(form: UseFormReturn<T>): boolean => {
    const { dirtyFields } = form.formState;

    // Cast to any since we're checking for profile-specific fields
    const dirtyFieldsAny = dirtyFields as any;
    if (dirtyFieldsAny.jobPosition) {
      return true;
    }

    if (!dirtyFieldsAny.dataV02) {
      return false;
    }

    const dataV02Dirty = dirtyFieldsAny.dataV02;

    const hasCompanyChanges = dataV02Dirty?.companies && Object.keys(dataV02Dirty.companies).length > 0;
    const hasEducationChanges = dataV02Dirty?.education && Object.keys(dataV02Dirty.education).length > 0;
    const hasSkillChanges =
      dataV02Dirty?.person?.profile?.skills && Object.keys(dataV02Dirty.person.profile.skills).length > 0;
    const hasCertificationChanges =
      dataV02Dirty?.others?.licenses_or_certifications &&
      Object.keys(dataV02Dirty.others.licenses_or_certifications).length > 0;

    return !!(hasCompanyChanges || hasEducationChanges || hasSkillChanges || hasCertificationChanges);
  };

  /**
   * Trigger worth data recalculation
   */
  const triggerRecalculation = async () => {
    await calculateWorthData({});

    // Optimistically set status to enqueued for immediate UI feedback
    queryClient.setQueryData(WorthDataQueries.keys.get(), (oldData: CommonModels.WorthNumbersDto | undefined) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        status: CommonModels.StatusEnum.processing,
      };
    });

    await queryClient.invalidateQueries({ queryKey: WorthDataQueries.keys.get() });
    await queryClient.invalidateQueries({ queryKey: ProfileAnalyticsQueries.keys.all });
  };

  return {
    hasWorthImpactingChanges,
    triggerRecalculation,
    isRecalculating,
  };
};
